import Head from 'next/head';
import Image from 'next/image';
import {useState} from 'react';
import styled from 'styled-components';
import {TFunction} from 'next-i18next';
import {GetServerSideProps} from 'next';
import {useTranslation} from 'next-i18next';
import {QueryClient} from '@tanstack/react-query';
import {
  BackgroundGradient,
  HandWriting,
  HowItWorksSection,
  LandingBigSecondaryButton,
  LandingSubHeading,
  MainTitle,
  SectionTitleLegacy
} from 'src/components/landingStyledComponents';
import TouchpointEmailExampleGraphic from 'src/components/landing/TouchpointEmailExampleGraphic';
import LandingDataProtectionSection from 'src/components/landing/LandingDataProtectionSection';
import TouchpointQRExampleGraphic from 'src/components/landing/TouchpointQRExampleGraphic';
import PanelistaButtonExplainer from 'src/components/landing/PanelistaButtonExplainer';
import FeatureExampleSection from 'src/components/landing/FeatureExampleSection';
import CirclesBackground from 'src/components/landing/CirclesBackground';
import LandingUISection from 'src/components/landing/LandingUISection';
import HowItWorksStep from 'src/components/landing/HowItWorksStep';
import LandingHeader from 'src/components/landing/LandingHeader';
import LandingFooter from 'src/components/landing/LandingFooter';
import RealWidget from 'src/components/touchpoints/RealWidget';
import {getPublicLayout} from 'src/components/PublicLayout';
import {nsTranslation, nsWebsite} from 'src/services/i18n';
import DemoBanner from 'src/components/landing/DemoBanner';
import {panelistaPrimaryColor} from 'src/utils/constants';
import ssrTranslations from 'src/utils/ssrTranslations';
import {ContentContainer} from './panels';

const ExampleContainer = styled.div`
  position: relative;
`;

const FirstExampleContainer = styled(ExampleContainer)`
  @media (max-width: 700px) {
    margin-top: 105px;
    margin-bottom: 60px;
  }
`;

const ThirdExampleContainer = styled(ExampleContainer)`
  @media (max-width: 700px) {
    margin-top: 40px;
    margin-bottom: -10px;
  }
`;

const StyledDrawnArrow = styled.img`
  position: absolute;
  top: -100px;
  right: 7%;
  transform: rotate(10deg);
`;

const StyledHandwriting = styled(HandWriting)`
  position: absolute;
  top: -135px;
  right: -6%;
  transform: rotate(-6deg);
  color: ${panelistaPrimaryColor};

  @media (max-width: 430px) {
    right: 0;
  }
`;

const ExamplesContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 60px;

  @media (max-width: 700px) {
    padding-bottom: 0;
  }

  .firstExample {
    @media (max-width: 450px) {
      min-height: initial;

      .graphic-side {
        padding: 0;
      }
    }
  }
`;

const QrCodeExampleContainer = styled(ExamplesContainer)`
  @media (max-width: 700px) {
    margin-top: 80px;
  }

  @media (max-width: 450px) {
    margin-top: 40px;
  }
`;

const OverflowContainer = styled.div`
  width: 100%;
  overflow: hidden;
`;

const WidgetContainer = styled.div`
  position: relative;
  filter: drop-shadow(0 1px 16px rgba(0, 0, 0, 0.1));
`;

const getUiShowcaseContent = (t: TFunction) => [
  {
    key: 'getFeedback',
    tabTitle: t('website.get feedback tab'),
    title: t('website.place touchpoint tab title'),
    description: t('website.place touchpoint tab description'),
    videoUrl: 'https://panelista.s3.nl-ams.scw.cloud/website/touchpointGetFeedbackSv.mp4'
  },
  {
    key: 'analyzeFeedback',
    tabTitle: t('website.discussion tab'),
    title: t('website.discussion tab title'),
    description: t('website.discussion tab description'),
    videoUrl: 'https://panelista.s3.nl-ams.scw.cloud/website/touchpointAnalyzeFeedbackSv.mp4'
  }
];

const Touchpoints = () => {
  const {t, i18n} = useTranslation();

  const [panelistaButtonActive, setPanelistaButton] = useState(false);

  const widgetTouchpointId = i18n.language === 'sv' ? '6183db02a47b4700167464a8' : '6183db87a47b470016746530';

  return (
    <>
      <Head>
        <title>{t('website.touchpoints page title')}</title>
        <meta key="og:title" content={t('website.touchpoints page title')} property="og:title" />
        <meta name="description" content={t('website.touchpoints page subheading')} key="description" />
        <meta key="og:description" content={t('website.touchpoints page subheading')} property="og:description" />
      </Head>

      <BackgroundGradient />

      <LandingHeader />

      <ContentContainer>
        <MainTitle>{t('website.touchpoints page title')}</MainTitle>
        <LandingSubHeading>{t('website.touchpoints page subheading')}</LandingSubHeading>
      </ContentContainer>

      <OverflowContainer>
        <ExamplesContainer>
          <FeatureExampleSection
            title={t('website.touchpoint example one title')}
            description={t('website.touchpoint example one description')}
            extraComponent={
              <LandingBigSecondaryButton
                as="button"
                style={{marginTop: 30, opacity: panelistaButtonActive ? 0 : 1}}
                onClick={() => setPanelistaButton(!panelistaButtonActive)}
              >
                {t('website.panelista button label')}
              </LandingBigSecondaryButton>
            }
            graphic={
              <FirstExampleContainer>
                <CirclesBackground />

                <WidgetContainer>
                  <RealWidget touchpointId={widgetTouchpointId} />
                </WidgetContainer>

                <StyledDrawnArrow src="/images/drawnArrow.svg" />
                <StyledHandwriting>{t('website.try it out')}</StyledHandwriting>
              </FirstExampleContainer>
            }
            className="firstExample"
          />

          <PanelistaButtonExplainer isVisible={panelistaButtonActive} close={() => setPanelistaButton(false)} />

          <FeatureExampleSection
            flipped
            title={t('website.touchpoint example two title')}
            description={t('website.touchpoint example two description')}
            bgColor="#f2fcfd"
            graphic={
              <QrCodeExampleContainer>
                <CirclesBackground />
                <TouchpointQRExampleGraphic />
              </QrCodeExampleContainer>
            }
          />

          <FeatureExampleSection
            title={t('website.touchpoint example three title')}
            description={t('website.touchpoint example three description')}
            graphic={
              <ThirdExampleContainer>
                <CirclesBackground />
                <TouchpointEmailExampleGraphic />
              </ThirdExampleContainer>
            }
          />
        </ExamplesContainer>
      </OverflowContainer>

      <HowItWorksSection>
        <SectionTitleLegacy>{t('website.how it works title')}</SectionTitleLegacy>

        <HowItWorksStep
          index={1}
          title={t('website.touchpoints step one title')}
          description={t('website.touchpoints step one description')}
          graphic={
            <Image
              src="/images/touchpointsStepOneGraphic.png"
              alt={t('website.touchpoints step one title')}
              width={374}
              height={242.5}
              quality={92}
            />
          }
        />

        <HowItWorksStep
          index={2}
          title={t('website.touchpoints step two title')}
          description={t('website.touchpoints step two description')}
          graphic={
            <Image
              src="/images/touchpointsStepTwoGraphic.png"
              alt={t('website.touchpoints step two title')}
              width={312}
              height={298}
              quality={95}
            />
          }
        />
      </HowItWorksSection>

      <LandingUISection uiContents={getUiShowcaseContent(t)} analyticsPlace="Touchpoints" />
      <LandingDataProtectionSection />
      <DemoBanner />

      <LandingFooter />
    </>
  );
};

Touchpoints.isPublicPage = true;

Touchpoints.getLayout = getPublicLayout;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const queryClient = new QueryClient();

  return {props: {...(await ssrTranslations(context, queryClient, [nsTranslation, nsWebsite]))}};
};

export default Touchpoints;
